// Compresses an image file to JPEG using canvas. Falls back to original on error.
export async function compressToJpeg(
  file: File,
  maxWidth = 1920,
  quality = 0.82,
): Promise<File> {
  return new Promise((resolve) => {
    const img = new Image()
    const src = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(src)
      let w = img.naturalWidth, h = img.naturalHeight
      if (w > maxWidth) { h = Math.round(h * maxWidth / w); w = maxWidth }
      const canvas = document.createElement('canvas')
      canvas.width = w; canvas.height = h
      canvas.getContext('2d')!.drawImage(img, 0, 0, w, h)
      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve(file); return }
          const name = file.name.replace(/\.[^.]+$/, '') + '.jpg'
          resolve(new File([blob], name, { type: 'image/jpeg' }))
        },
        'image/jpeg',
        quality,
      )
    }
    img.onerror = () => { URL.revokeObjectURL(src); resolve(file) }
    img.src = src
  })
}

export function isCompressibleImage(mimeType: string): boolean {
  return (
    mimeType.startsWith('image/') &&
    !mimeType.includes('heic') &&
    !mimeType.includes('heif') &&
    !mimeType.includes('gif') &&
    !mimeType.includes('svg')
  )
}

// Picks best supported WebM MIME type for MediaRecorder.
function bestVideoMime(): string {
  const types = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ]
  return types.find((t) => MediaRecorder.isTypeSupported(t)) ?? 'video/webm'
}

// Re-encodes a video via canvas + MediaRecorder at lower resolution/bitrate.
// onProgress receives 0–100 while processing.
// Falls back to original if: file is small, browser doesn't support re-encoding,
// or the output ends up larger than the original.
export async function compressVideo(
  file: File,
  onProgress?: (pct: number) => void,
  maxWidth = 1280,
  maxHeight = 720,
  bitrate = 2_000_000, // 2 Mbps
): Promise<File> {
  // Skip small files
  if (file.size <= 20 * 1024 * 1024) return file

  if (typeof MediaRecorder === 'undefined') return file

  const mimeType = bestVideoMime()

  return new Promise((resolve) => {
    const video = document.createElement('video')
    video.muted = true
    video.playsInline = true
    const src = URL.createObjectURL(file)
    video.src = src

    video.onloadedmetadata = async () => {
      let w = video.videoWidth, h = video.videoHeight
      if (w > maxWidth) { h = Math.round(h * maxWidth / w); w = maxWidth }
      if (h > maxHeight) { w = Math.round(w * maxHeight / h); h = maxHeight }
      // Ensure even dimensions (required by some codecs)
      w = Math.floor(w / 2) * 2
      h = Math.floor(h / 2) * 2

      const canvas = document.createElement('canvas')
      canvas.width = w; canvas.height = h
      const ctx = canvas.getContext('2d')!

      let recorder: MediaRecorder
      try {
        const stream = canvas.captureStream(30)
        recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: bitrate })
      } catch {
        URL.revokeObjectURL(src)
        resolve(file)
        return
      }

      const chunks: BlobPart[] = []
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data) }

      recorder.onstop = () => {
        URL.revokeObjectURL(src)
        const containerType = mimeType.split(';')[0]
        const blob = new Blob(chunks, { type: containerType })
        const ext = '.webm'
        const name = file.name.replace(/\.[^.]+$/, '') + ext
        // Use compressed only if smaller
        resolve(blob.size < file.size
          ? new File([blob], name, { type: containerType })
          : file)
      }

      recorder.start(200)

      try {
        await video.play()
      } catch {
        recorder.stop()
        URL.revokeObjectURL(src)
        resolve(file)
        return
      }

      const duration = video.duration || 1

      const draw = () => {
        if (video.ended || video.paused) {
          if (recorder.state === 'recording') recorder.stop()
          return
        }
        ctx.drawImage(video, 0, 0, w, h)
        onProgress?.(Math.min(99, Math.round((video.currentTime / duration) * 100)))
        requestAnimationFrame(draw)
      }
      draw()

      video.onended = () => {
        if (recorder.state === 'recording') recorder.stop()
        onProgress?.(100)
      }
    }

    video.onerror = () => {
      URL.revokeObjectURL(src)
      resolve(file)
    }
  })
}
